import { faArrowRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion, PanInfo, useAnimation } from 'motion/react';
import { useCallback, useRef, useState } from 'react';

interface SlideToConfirmButtonProps {
  onConfirm: () => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
  confirmText?: string;
  slideDistance?: number;
}

export function SlideToConfirmButton({
  onConfirm,
  disabled = false,
  className = '',
  children,
  confirmText = 'Release to confirm',
  slideDistance = 200,
}: SlideToConfirmButtonProps) {
  const [isSliding, setIsSliding] = useState(false);
  const [slideProgress, setSlideProgress] = useState(0);
  const controls = useAnimation();
  const containerRef = useRef<HTMLDivElement>(null);

  const handlePanStart = useCallback(() => {
    if (disabled) {
      return;
    }
    setIsSliding(true);
  }, [disabled]);

  const handlePan = useCallback(
    (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (disabled) {
        return;
      }

      const containerWidth = containerRef.current?.offsetWidth ?? slideDistance;
      const maxSlide = containerWidth - 57; // Account for slider thumb width (56px) + padding
      const currentX = Math.max(0, Math.min(info.offset.x, maxSlide));
      const progress = currentX / maxSlide;

      setSlideProgress(progress);
      controls.set({ x: currentX });
    },
    [disabled, slideDistance, controls],
  );

  const handlePanEnd = useCallback(
    (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (disabled) {
        return;
      }

      const containerWidth = containerRef.current?.offsetWidth ?? slideDistance;
      const maxSlide = containerWidth - 57;
      const currentX = Math.max(0, Math.min(info.offset.x, maxSlide));
      const progress = currentX / maxSlide;

      if (progress >= 0.8) {
        // Confirmed - slide completed
        onConfirm();
        // Reset after confirmation
        setTimeout(() => {
          controls.start({ x: 0 });
          setSlideProgress(0);
          setIsSliding(false);
        }, 200);
      } else {
        // Not confirmed - slide back
        controls.start({ x: 0 });
        setSlideProgress(0);
        setIsSliding(false);
      }
    },
    [disabled, slideDistance, controls, onConfirm],
  );

  return (
    <div
      ref={containerRef}
      data-testid="slide-to-confirm-container"
      className={`relative h-15 rounded-lg overflow-hidden select-none ${className}`}
      style={{
        background: disabled
          ? 'bg-surface/80'
          : `linear-gradient(90deg,
              rgba(255,255,255,0.1) 0%,
              rgba(255,255,255,${0.1 + slideProgress * 0.2}) ${slideProgress * 100}%,
              rgba(255,255,255,0.05) 100%)`,
      }}
    >
      {/* Background track */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className={`text-sm font-medium transition-opacity duration-200 ${
            disabled
              ? 'text-label'
              : isSliding && slideProgress > 0.5
                ? 'text-white opacity-100'
                : 'text-white/70 opacity-70'
          }`}
        >
          {isSliding && slideProgress > 0.5 ? confirmText : children}
        </span>
      </div>

      {/* Target icon on right side */}
      {!disabled && !isSliding && (
        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 opacity-60">
          <FontAwesomeIcon icon={faCheck} className="text-white text-sm" />
        </div>
      )}

      {/* Progress indicator */}
      <div
        className="absolute inset-y-0 left-0 transition-all duration-200 ease-out"
        style={{
          width: `${slideProgress * 100}%`,
          background: disabled
            ? 'transparent'
            : slideProgress > 0.25
              ? 'rgba(var(--brand-green-rgb), 0.3)'
              : 'rgba(255, 255, 255, 0.1)',
        }}
      />

      {/* Sliding thumb */}
      {!disabled && (
        <motion.div
          data-testid="slide-thumb"
          className="absolute left-1 top-1 bottom-1 w-14 bg-white rounded-md flex items-center justify-center cursor-grab active:cursor-grabbing shadow-lg"
          style={{
            background: slideProgress > 0.25 ? 'var(--brand-green)' : 'white',
          }}
          animate={controls}
          onPanStart={handlePanStart}
          onPan={handlePan}
          onPanEnd={handlePanEnd}
          drag="x"
          dragConstraints={containerRef}
          dragElastic={0.1}
          whileDrag={{ scale: 1.05 }}
        >
          <FontAwesomeIcon
            icon={faArrowRight}
            className={`text-sm transition-colors duration-200 ${
              slideProgress > 0.25 ? 'text-black' : 'text-gray-500'
            }`}
          />
        </motion.div>
      )}
    </div>
  );
}
